import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  open: boolean;
  projectName: string;
  label: string; // e.g. "Internprosjekt" or "Timeprosjekt (T&M)"
  /** VIB-1287: antall netter (null = ingen overnatting). */
  onSave: (comment: string, overnightCount: number | null) => void;
  onCancel: () => void;
}

export function TimerStopCommentDialog({ open, projectName, label, onSave, onCancel }: Props) {
  const [comment, setComment] = useState("");
  // VIB-1287: overnatting på alle føringer (Erik 15.07)
  const [overnight, setOvernight] = useState(false);
  const [nights, setNights] = useState("1");

  const reset = () => {
    setComment("");
    setOvernight(false);
    setNights("1");
  };

  const handleSave = () => {
    if (!comment.trim()) return;
    onSave(comment.trim(), overnight ? Math.max(1, Number(nights) || 1) : null);
    reset();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Kommentar påkrevd</DialogTitle>
          <DialogDescription>
            {label} <strong>«{projectName}»</strong> krever kommentar på
            timeregistreringen. Beskriv hva du brukte tiden til.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Hva brukte du tiden til?"
          className="resize-none"
          rows={3}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSave();
            }
          }}
        />
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={overnight}
              onChange={(e) => setOvernight(e.target.checked)}
              className="h-4 w-4 accent-primary"
              aria-label="Overnatting"
            />
            Overnatting
          </label>
          {overnight && (
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min={1}
                value={nights}
                onChange={(e) => setNights(e.target.value)}
                className="h-8 w-16 rounded-md border bg-background px-2 text-sm text-right"
                aria-label="Antall netter"
              />
              <span className="text-sm text-muted-foreground">netter</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel}>
            Avbryt (ikke lagre)
          </Button>
          <Button onClick={handleSave} disabled={!comment.trim()}>
            Lagre timer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
